// Builds the payload for the SII backend (CL).
// Pure function extracted from DocumentsCreate.vue (setPayloadForBackend method).
// Every dependency previously read from `this.*` is now received via the `input`
// object, so the payload assembly can be tested in isolation.
import { EXPORT_TYPES, OTHER_CURRENCY_TYPES, EXEMPT_TYPES } from '@/collections/documentTypeGroups'
import { cleanRut } from '@/utils/rut'
import moment from 'moment'

// currency_info block (CLP for export; foreign currency for other-currency types).
// Only includes amounts that are > 0, matching the original behavior.
function buildCurrencyInfo (aggregations, name, rate, toCurrency) {
  return {
    name,
    exchange_rate: rate.toString(),
    ...(aggregations.amount > 0 && { amount_net: toCurrency(aggregations.amount) }),
    ...(aggregations.exempt > 0 && { amount_exempt: toCurrency(aggregations.exempt) }),
    ...(aggregations.total > 0 && { amount_total: toCurrency(aggregations.total) })
  }
}

export function buildClDocumentPayload ({
  payload = {},
  documents,
  aggregations,
  activeDocument,
  exchange,
  currencies,
  saleModeCode,
  isActiveNetAmounts,
  isActiveGrossAmounts,
  isExtraEmail,
  hasOtherCurrencyInfo,
  isTest
}) {
  const convertAmounts = (amount, rate) => amount * rate
  return {
    document_type: payload.document_type,
    test: isTest,
    numbering: !payload?.numbering,
    document: {
      ...(isActiveNetAmounts && { net_ind: isActiveNetAmounts }),
      ...(isActiveGrossAmounts && { net_ind: false }),
      ...(EXPORT_TYPES.includes(payload?.document_type) && {
        currency: payload?.exchange?.currency_from
      }),
      ...(payload.service_type && !['CL52'].includes(payload?.document_type) && { service_type: payload.service_type }),
      ...(payload?.number?.toString() !== '' && { number: payload?.number?.toString() }),
      issued_date_time: moment(payload?.date, 'YYYY-MM-DD').format('YYYY-MM-DD[T]00:00:00'),
      ...(payload.document_type !== 'CL52' && { due_date: payload?.due_date }),
      ...(payload?.period_start && { from_date: payload?.period_start }),
      ...(payload?.period_end && { to_date: payload?.period_end }),
      payment_method: payload?.collection_method?.toString(),
      ...(payload?.note !== '' && { payment_terms_description: payload?.note }),
      ...(payload.document_type === 'CL52' && { shipment_ind: payload?.shipping?.type.toString() }),
      amount: {
        ...(aggregations.irs_tax > 0 && { tax: aggregations.irs_tax?.toString() }),
        ...(!EXEMPT_TYPES.includes(payload.document_type) && { net: aggregations.amount?.toString() }),
        ...(aggregations.discount > 0 && (documents.discount_scheme === '%' ? { discount_percentage: documents.discount?.toString() } : { discount: aggregations.discount?.toString() })),
        subtotal: aggregations.subtotal?.toString(),
        total: aggregations.total?.toString(),
        vat: aggregations.taxes?.toString(),
        vat_rate: activeDocument.tax?.toString(),
        exempt: aggregations.exempt?.toString()
      },
      customer: {
        name: payload?.customer?.name,
        tax_id: payload?.customer?.taxpayer,
        activity: payload?.customer?.business_activity,
        address: payload?.customer?.address,
        ...(payload?.customer?.email !== '' && { contact_email: payload?.customer?.email }),
        ...(payload?.customer?.state !== '' && { municipality: payload?.customer?.state }),
        ...(payload?.customer?.city !== '' && { city: payload?.customer?.city }),
        ...(payload?.customer?.contacts !== '' && { contact_name: payload?.customer?.contacts }),
        ...(EXPORT_TYPES.includes(payload?.document_type) && {
          ...(payload?.customer?.foreign_country && { foreign_country: payload?.customer?.foreign_country?.toString() }),
          ...(payload?.customer?.foreign_id && { foreign_id: payload?.customer?.foreign_id?.toString() }),
          ...(payload?.customer?.foreign_additional_id && { foreign_additional_id: payload?.customer?.foreign_additional_id })
        })
      },
      ...(payload.document_type === 'CL52' && payload?.shipping?.type.toString() !== '' && {
        shipment: {
        ...(payload?.shipping?.company_tax_id !== '' && { carrier_tax_id: payload?.shipping?.company_tax_id }),
        ...(payload?.shipping?.address !== '' && { delivery_address: payload?.shipping?.address }),
        ...(payload?.shipping?.state !== '' && { delivery_municipality: payload?.shipping?.state }),
        ...(payload?.shipping?.city !== '' && { delivery_city: payload?.shipping?.city }),
        driver_name: payload?.shipping?.carrier_name,
        ...(payload?.shipping?.carrier_id !== '' && { driver_tax_id: payload?.shipping?.carrier_id }),
        registration_plate: payload?.shipping?.licence_plate,
        ...(payload?.shipping?.trailer_plate !== '' && { trailer_plate: payload?.shipping?.trailer_plate }),
        ...(payload?.shipping?.departure_date !== '' && { departure_date: payload?.shipping?.departure_date }),
        ...(payload?.shipping?.departure_time && payload?.shipping?.departure_time !== '' && { departure_time: `${payload?.shipping?.departure_time}:00` }),
        ...(payload?.shipping?.arrival_date !== '' && { arrival_date: payload?.shipping?.arrival_date })
        }
      }),
      ...(payload?.issuer?.branch || (isExtraEmail && payload?.customer?.email?.length > 0)) && {
        extra: {
          ...(payload?.issuer?.branch && { branch_id: payload?.issuer?.branch }),
          ...(isExtraEmail && payload?.customer?.email?.length > 0 && { email: payload?.customer?.email })
        }
      },
      items: payload?.items?.map((line, index) => ({
        line: index + 1,
        name: line?.name,
        code: line?.code,
        code_name: line?.code_scheme,
        ...(line?.unit_label !== '' && { unit: line?.unit_label }),
        amount: aggregations.lines[index].totalItem.toString(),
        price: line?.amount?.toString(),
        quantity: line?.quantity?.toString(),
        ...(line?.discount > 0 && (line?.discount_scheme === '%' ? { discount_percentage: line?.discount?.toString() } : { discount: line?.discount?.toString() })),
        ...(line?.irs_tax_code > 0 && { tax_codes: [line?.irs_tax_code?.toString()] }),
        ...(line?.description !== '' && { description: line?.description }),
        ...(line.exempt && { exempt_ind: 1 }),
        ...(payload.document_type === 'CL43' && { document_type: line?.document_type }),
        ...(OTHER_CURRENCY_TYPES.includes(payload.document_type) && hasOtherCurrencyInfo && {
          other_currency: {
            currency: exchange.currency_from,
            rate: exchange.rate.toString(),
            price: (Number(line?.amount) / Number(exchange.rate)).toFixed(2),
            amount: (Number(aggregations.lines[index].totalItem) / Number(exchange.rate)).toFixed(2)
          }
        })
      })) ?? [],
      ...(payload?.references?.length && {
        references: payload?.references?.map((ref, index) => ({
          line: index + 1,
          date: ref.date,
          reason: ref.description,
          number: ref.serial?.toString(),
          code: ref.cod_ref?.toString(),
          type: ref.reference_type,
          ...(ref.ind_global !== 0 && { global_ind: ref.ind_global }),
          ...(index === 0 && ['CL56', 'CL61'].includes(payload?.document_type) && ref.other_tax_id && { other_tax_id: cleanRut(ref.other_tax_id) })
        }))
      }),
      ...(EXPORT_TYPES.includes(payload?.document_type) && {
        currency_info: buildCurrencyInfo(aggregations, 'PESO CL', payload?.exchange?.rate, v => convertAmounts(v, payload?.exchange?.rate).toString()),
        shipment: {
          customs: {
            sale_mode_code: saleModeCode
          }
        }
      }),
      ...(OTHER_CURRENCY_TYPES.includes(payload?.document_type) && hasOtherCurrencyInfo && {
        currency_info: buildCurrencyInfo(
          aggregations,
          currencies.find(c => c.code === exchange.currency_from)?.name || exchange.currency_from,
          exchange.rate,
          v => (v / Number(exchange.rate)).toFixed(2)
        )
      })
    }
  }
}
